/**
 * PublishPromptPackDialog - Modal for publishing prompt packs to BrainDance store
 * Allows optional linking to a parent DynamicBook
 */
import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';

import { dynamicBooksApi, storeApi, BookSummary, PublishSKURequest } from '../services/api';
import { getUserSub } from '../services/tokenResolver';

interface PublishPromptPackDialogProps {
  open: boolean;
  onClose: () => void;
  pack: {
    entityId: string;
    promptPackName: string;
    description?: string;
    price?: number;
  } | null;
  onSuccess?: (result: { productId: string; name: string }) => void;
}

export default function PublishPromptPackDialog({
  open,
  onClose,
  pack,
  onSuccess,
}: PublishPromptPackDialogProps) {
  const [books, setBooks] = useState<BookSummary[]>([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [booksError, setBooksError] = useState<string | null>(null);
  const [selectedBookId, setSelectedBookId] = useState<string>('');
  const [priceInCents, setPriceInCents] = useState<number>(0);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);

  // Fetch user's books when dialog opens
  useEffect(() => {
    const fetchBooks = async () => {
      if (!open) return;
      setBooksLoading(true);
      setBooksError(null);
      try {
        const result = await dynamicBooksApi.listAuthoredBooks();
        // Filter to only show published books with valid IDs (they can be parents)
        const publishedBooks = (result.data || result || []).filter(
          (book: BookSummary) => book.status === 'published' && book.bookId
        );
        setBooks(publishedBooks);
      } catch (err) {
        console.error('Failed to load books:', err);
        setBooksError('Could not load your books. You can still publish without linking to a book.');
        setBooks([]);
      } finally {
        setBooksLoading(false);
      }
    };
    fetchBooks();
  }, [open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open && pack) {
      // Initialize price from pack price (convert to cents if needed)
      setPriceInCents(pack.price ? Math.round(pack.price * 100) : 0);
      setSelectedBookId('');
      setPublishError(null);
      setPublishSuccess(null);
    }
  }, [open, pack]);

  const handlePublish = async () => {
    if (!pack) return;

    const authorId = await getUserSub();
    if (!authorId) {
      setPublishError('Not authenticated. Please sign in again.');
      return;
    }

    setPublishing(true);
    setPublishError(null);
    setPublishSuccess(null);

    try {
      const request: PublishSKURequest = {
        // For prompt-packs, we use the pack's entityId as the bookId
        // The API expects BOOK# prefix but we'll construct it with PACK#
        bookId: `PACK#${pack.entityId}`,
        authorId: authorId,
        productType: 'prompt-pack',
        priceInCents: priceInCents,
      };

      // If a parent book is selected, add parentProductId
      if (selectedBookId) {
        // The parentProductId should be the book's catalog product ID
        // which is typically the book's UUID without the BOOK# prefix
        request.parentProductId = selectedBookId;
      }

      const result = await storeApi.publishSKU(request);
      setPublishSuccess(`Successfully published "${result.name}" to the store!`);
      onSuccess?.({ productId: result.productId, name: result.name });
    } catch (err: unknown) {
      console.error('Failed to publish:', err);
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setPublishError(
        error.response?.data?.message ||
        error.message ||
        'Failed to publish prompt pack. Please try again.'
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleClose = () => {
    if (publishing) return; // Prevent closing while publishing
    onClose();
  };

  const formatPrice = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PublishIcon color="primary" />
        Publish to BrainDance Store
      </DialogTitle>
      <DialogContent>
        {pack && (
          <Box sx={{ pt: 1 }}>
            {/* Pack Info */}
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              {pack.promptPackName}
            </Typography>
            {pack.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pack.description}
              </Typography>
            )}

            {/* Price Input */}
            <TextField
              fullWidth
              label="Price (USD)"
              type="number"
              value={priceInCents / 100}
              onChange={(e) => {
                const dollars = parseFloat(e.target.value) || 0;
                setPriceInCents(Math.round(dollars * 100));
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={priceInCents === 0 ? 'Free' : formatPrice(priceInCents)}
              sx={{ mb: 3 }}
            />

            {/* Parent Book Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="parent-book-label">Link to DynamicBook (Optional)</InputLabel>
              <Select
                labelId="parent-book-label"
                value={selectedBookId}
                onChange={(e) => setSelectedBookId(e.target.value as string || '')}
                label="Link to DynamicBook (Optional)"
                disabled={booksLoading}
              >
                <MenuItem key="none" value="">
                  <em>None - Standalone prompt pack</em>
                </MenuItem>
                {books.map((book) => (
                  <MenuItem key={book.bookId} value={book.bookId || ''}>
                    {book.title}
                  </MenuItem>
                ))}
              </Select>
              {booksLoading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Loading your books...
                </Typography>
              )}
            </FormControl>

            {booksError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {booksError}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary">
              Linking to a DynamicBook groups this prompt pack with that book in the store.
              Standalone prompt packs are listed independently.
            </Typography>

            {/* Status Messages */}
            {publishError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {publishError}
              </Alert>
            )}

            {publishSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {publishSuccess}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={publishing}>
          {publishSuccess ? 'Close' : 'Cancel'}
        </Button>
        {!publishSuccess && (
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={publishing || !pack}
            startIcon={publishing ? <CircularProgress size={20} /> : <PublishIcon />}
          >
            {publishing ? 'Publishing...' : 'Publish'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
